import React, { useRef } from 'react';
import { MessageCircle, Clock, Tag, ChevronLeft, ChevronRight } from 'lucide-react';

function brl(n) { return `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`; }

function waUrl(it) {
  const num = (it.whatsappPhone || '').replace(/\D/g, '');
  const price = it.salePrice > 0 ? it.salePrice : it.price;
  const defaultMsg = `Olá! Quero saber mais sobre: *${it.name}*${price ? ` — ${brl(price)}` : ''}`;
  const msg = encodeURIComponent(it.whatsappMessage || defaultMsg);
  if (!num) return 'https://wa.me/';
  return `https://wa.me/${num}?text=${msg}`;
}

export default function Showcase({ showcase, theme, onItemClick }) {
  const scrollerRef = useRef(null);
  if (!showcase || !showcase.enabled) return null;
  const items = (showcase.items || [])
    .filter((it) => it.enabled !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  if (items.length === 0) return null;

  const scrollBy = (dx) => scrollerRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  return (
    <section className="mt-8 fade-in-up">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-lg font-display font-bold" style={{ color: theme.text }}>{showcase.title || 'Serviços & Produtos'}</h2>
        <div className="hidden sm:flex items-center gap-1">
          <button onClick={() => scrollBy(-320)} className="p-1.5 rounded-full bg-black/15 hover:bg-black/30 transition" style={{ color: theme.text }} aria-label="anterior"><ChevronLeft size={16} /></button>
          <button onClick={() => scrollBy(320)} className="p-1.5 rounded-full bg-black/15 hover:bg-black/30 transition" style={{ color: theme.text }} aria-label="próximo"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto overflow-y-visible snap-x snap-mandatory -mx-5 px-5 pb-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((it) => {
          const hasPromo = it.salePrice > 0 && it.salePrice < it.price;
          const displayPrice = hasPromo ? it.salePrice : it.price;
          return (
            <article key={it.id} className="relative shrink-0 snap-start w-[220px] bg-white rounded-2xl overflow-hidden shadow-lg">
              <div className="relative aspect-[4/5] w-full bg-neutral-200">
                {it.image ? (
                  <img src={it.image} alt={it.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-neutral-400"><Tag size={32} /></div>
                )}
                {displayPrice > 0 && (
                  <div className="absolute top-3 left-3 bg-white rounded-full px-3 py-1 shadow-md">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-sm font-bold text-neutral-900">{brl(displayPrice)}</span>
                      {hasPromo && <span className="text-[10px] text-neutral-400 line-through">{brl(it.price)}</span>}
                    </div>
                  </div>
                )}
                {it.type === 'service' && it.durationMin > 0 && (
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white rounded-full px-2.5 py-1 text-[10px] font-semibold flex items-center gap-1">
                    <Clock size={10} /> {it.durationMin}min
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className="font-bold text-sm leading-tight line-clamp-2">{it.name}</div>
                  {it.category && <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{it.category}</div>}
                </div>
              </div>
              <div className="p-3 space-y-2">
                {it.description && <p className="text-xs text-neutral-600 line-clamp-2 min-h-[2rem]">{it.description}</p>}
                <a
                  href={waUrl(it)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => onItemClick && onItemClick(it)}
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2 text-xs font-semibold transition"
                >
                  <MessageCircle size={12} /> Falar no WhatsApp
                </a>
              </div>
            </article>
          );
        })}
        <div className="shrink-0 w-1" />
      </div>
    </section>
  );
}
